import { z } from "zod";

// #region UserId

const USER_SOURCE_CODES = {
  vision_auth: "2",
  legacy_app: "3",
} as const;

export type UserSource = keyof typeof USER_SOURCE_CODES;

const REVERSE_USER_SOURCE_CODES: Record<string, UserSource> = {
  "2": "vision_auth",
  "3": "legacy_app",
} as const;

const USER_MAX_UNIQUE_LENGTH = 128;
const USER_UNIQUE_PATTERN = /^[a-zA-Z0-9|_-]+$/;

declare const UserIdBrand: unique symbol;
declare const UserIdSourceBrand: unique symbol;

/**
 * A unique identifier for a User.
 *
 * Structure:
 * - Source: the source system ('vision_auth' or 'legacy_app')
 * - Unique: a URL-safe string (1-128 chars, [a-zA-Z0-9_-])
 */
export type UserId<S extends UserSource = UserSource> = string & {
  readonly [UserIdBrand]: "UserId";
  readonly [UserIdSourceBrand]: S;
};

export class UserIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format" | "InvalidSource",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Constructs a UserId from its component parts or throws a UserIdError.
 *
 * @param source - The source system ('vision_auth' or 'legacy_app')
 * @param unique - URL-safe string (1-128 chars, [a-zA-Z0-9|_-])
 */
const userIdFromParts = <S extends UserSource = UserSource>(source: S, unique: string): UserId<S> => {
  if (!(source in USER_SOURCE_CODES)) {
    throw new UserIdError(
      "InvalidSource",
      `Source ${source} is not valid. Expected one of: ${Object.keys(USER_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (typeof unique !== "string" || unique.length === 0) {
    throw new UserIdError("OutOfRange", `Unique string cannot be empty.`);
  }

  if (unique.length > USER_MAX_UNIQUE_LENGTH) {
    throw new UserIdError(
      "OutOfRange",
      `Unique string ${unique} exceeds maximum length. Expected at most ${String(USER_MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!USER_UNIQUE_PATTERN.test(unique)) {
    throw new UserIdError(
      "Format",
      `Unique string ${unique} contains invalid characters. Expected only [a-zA-Z0-9|_-].`,
    );
  }

  const sourceCode = USER_SOURCE_CODES[source];
  return `U${sourceCode}-${unique}` as UserId<S>;
};

/**
 * Canonical encoding is 'U' + source code + '-' + unique string.
 */
const userIdToString = (id: UserId): string => {
  return id as string;
};

/**
 * Parses a UserId from canonical encoding or throws a UserIdError.
 * (Accepts `/^U[23]-[a-zA-Z0-9|_-]{1,128}$/`.)
 */
const userIdFromString = (str: string): UserId => {
  if (typeof str !== "string" || str.length < 4) {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected format: U{source}-{unique}.`);
  }

  if (str.charAt(0) !== "U") {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected to start with 'U'.`);
  }

  const sourceCode = str.charAt(1);
  if (!(sourceCode in REVERSE_USER_SOURCE_CODES)) {
    throw new UserIdError(
      "InvalidSource",
      `String ${str} has invalid source code '${sourceCode}'. Expected one of: ${Object.keys(REVERSE_USER_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (str.charAt(2) !== "-") {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected '-' separator at position 2.`);
  }

  const unique = str.slice(3);

  if (unique.length === 0) {
    throw new UserIdError("OutOfRange", `String ${str} has empty unique part.`);
  }

  if (unique.length > USER_MAX_UNIQUE_LENGTH) {
    throw new UserIdError(
      "OutOfRange",
      `String ${str} has unique part that exceeds maximum length. Expected at most ${String(USER_MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!USER_UNIQUE_PATTERN.test(unique)) {
    throw new UserIdError(
      "Format",
      `String ${str} has unique part with invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  return str as UserId;
};

/**
 * Extracts the component parts from a UserId.
 *
 * @param id - The UserId to decompose
 * @returns An object with source and unique properties
 */
const userIdToParts = <S extends UserSource = UserSource>(id: UserId<S>): { source: S; unique: string } => {
  const str = id as string;
  const sourceCode = str.charAt(1);
  const source = REVERSE_USER_SOURCE_CODES[sourceCode];
  const unique = str.slice(3); // Skip 'U', source code, and '-'

  if (!source) {
    throw new UserIdError("InvalidSource", `UserId ${str} has invalid source code '${sourceCode}'.`);
  }

  return { source: source as S, unique };
};

/**
 * Extracts the source from a UserId.
 */
const userIdSource = <S extends UserSource = UserSource>(id: UserId<S>): S => {
  return userIdToParts(id).source;
};

/**
 * Extracts the unique part from a UserId.
 */
const userIdUnique = (id: UserId): string => {
  return userIdToParts(id).unique;
};

export const UserId = {
  fromParts: userIdFromParts,
  fromString: userIdFromString,
  toString: userIdToString,
  toParts: userIdToParts,
  source: userIdSource,
  unique: userIdUnique,
} as const;

export const UserIdSchema = z.codec(z.string(), z.custom<UserId>(), {
  decode: (encoded) => UserId.fromString(encoded),
  encode: (decoded) => UserId.toString(decoded),
});

// #endregion

// #region User Scopes

export type UserScope = { kind: "customer" | "branch" | "test"; key: string };

// #endregion
